import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createTranslator, type TranslationKey } from '../i18n';
import type { RegionApiResponse, RegionState, SiteDirection, SiteLanguage } from '../types/region';

const LANG_STORAGE_KEY = 'activelegend_site_lang';
const REGION_SESSION_KEY = 'activelegend_region_v3';

export type RegionContextValue = RegionState & {
  t: (key: TranslationKey | string, fallback?: string) => string;
  setLanguage: (lang: SiteLanguage) => void;
  toggleLanguage: () => void;
};

const RegionContext = createContext<RegionContextValue | null>(null);

/**
 * Priority Hierarchy for Language Selection:
 * 1. Explicit visitor selection in localStorage ('activelegend_site_lang')
 * 2. URL parameter (?lang=en or ?lang=fa or ?locale=...)
 * 3. Browser language preferences (navigator.languages / navigator.language)
 * 4. Fallback signal (IP geolocation / timezone)
 * 5. Configured studio default ('fa')
 */
export function determineInitialLanguage(): SiteLanguage {
  // 1 & 2: Local storage (Explicit previous selection)
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem(LANG_STORAGE_KEY);
      if (saved === 'fa' || saved === 'en') {
        return saved;
      }
    }
  } catch {}

  // 3: URL Parameter (?lang=en, ?lang=fa)
  try {
    if (typeof window !== 'undefined' && window.location) {
      const searchParams = new URLSearchParams(window.location.search);
      const urlLang = searchParams.get('lang') || searchParams.get('locale');
      if (urlLang) {
        const lower = urlLang.toLowerCase().trim();
        if (lower.startsWith('fa')) return 'fa';
        if (lower.startsWith('en')) return 'en';
      }
    }
  } catch {}

  // 4: TimeZone & Location signal (Users in Tehran/Iran timezones should always default to Persian)
  try {
    const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || '').toLowerCase();
    if (tz.includes('tehran') || tz.includes('iran')) {
      return 'fa';
    }
  } catch {}

  // 5: Browser language preferences
  try {
    if (typeof navigator !== 'undefined') {
      const browserLangs = navigator.languages || [navigator.language || ''];
      // Check if Persian exists anywhere in user's preferred languages
      const hasFa = browserLangs.some((raw) => {
        const l = (raw || '').toLowerCase().trim();
        return l.startsWith('fa') || l.startsWith('pes') || l.startsWith('prs');
      });
      if (hasFa) {
        return 'fa';
      }

      // If user is clearly an international visitor without Persian preferences
      const firstLang = (browserLangs[0] || '').toLowerCase().trim();
      if (firstLang.startsWith('en') || firstLang.startsWith('de') || firstLang.startsWith('fr')) {
        return 'en';
      }
    }
  } catch {}

  // 6: Default studio language
  return 'fa';
}

async function detectNetworkLocation(): Promise<RegionApiResponse> {
  // Check session storage first to avoid redundant requests
  try {
    const cached = sessionStorage.getItem(REGION_SESSION_KEY);
    if (cached) {
      return JSON.parse(cached) as RegionApiResponse;
    }
  } catch {}

  // 1. Cloudflare Pages Function
  try {
    const res = await fetch('/api/region', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (res.ok) {
      const data = (await res.json()) as RegionApiResponse;
      if (data && data.country && data.country !== 'XX') {
        const resObj: RegionApiResponse = {
          country: data.country.toUpperCase(),
          isIran: data.country.toUpperCase() === 'IR',
        };
        try { sessionStorage.setItem(REGION_SESSION_KEY, JSON.stringify(resObj)); } catch {}
        return resObj;
      }
    }
  } catch {}

  // 2. CORS fallback
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    const res = await fetch('https://api.country.is/', {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    clearTimeout(timeout);
    if (res.ok) {
      const data = await res.json();
      if (data && data.country) {
        const country = String(data.country).toUpperCase();
        const resObj: RegionApiResponse = { country, isIran: country === 'IR' };
        try { sessionStorage.setItem(REGION_SESSION_KEY, JSON.stringify(resObj)); } catch {}
        return resObj;
      }
    }
  } catch {}

  // Default network location
  return { country: 'US', isIran: false };
}

export function RegionProvider({ children }: { children: ReactNode }) {
  const initialLanguage = useMemo(() => determineInitialLanguage(), []);
  const initialDirection: SiteDirection = initialLanguage === 'fa' ? 'rtl' : 'ltr';

  const [language, setLanguageState] = useState<SiteLanguage>(initialLanguage);
  const [direction, setDirection] = useState<SiteDirection>(initialDirection);
  const [country, setCountry] = useState<string>('IR');
  const [isIran, setIsIran] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detectionFailed, setDetectionFailed] = useState<boolean>(false);

  const applyDocumentLocale = useCallback((lang: SiteLanguage, dir: SiteDirection) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.lang = lang === 'fa' ? 'fa-IR' : 'en-US';
    root.dir = dir;
    if (document.body) {
      document.body.dir = dir;
    }
  }, []);

  // Update language explicitly
  const setLanguage = useCallback((newLang: SiteLanguage) => {
    const newDir: SiteDirection = newLang === 'fa' ? 'rtl' : 'ltr';
    try {
      localStorage.setItem(LANG_STORAGE_KEY, newLang);
    } catch {}
    setLanguageState(newLang);
    setDirection(newDir);
    applyDocumentLocale(newLang, newDir);
  }, [applyDocumentLocale]);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'fa' ? 'en' : 'fa');
  }, [language, setLanguage]);

  // Initial apply of document locale
  useEffect(() => {
    applyDocumentLocale(language, direction);
  }, [language, direction, applyDocumentLocale]);

  // Network geolocation (for region-specific services without overriding explicit language)
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const run = async () => {
      try {
        const net = await detectNetworkLocation();
        setCountry(net.country);
        setIsIran(net.isIran);
      } catch {
        setDetectionFailed(true);
      }
    };

    void run();
  }, []);

  const t = useMemo(() => createTranslator(language), [language]);

  const value = useMemo<RegionContextValue>(
    () => ({
      country,
      isIran,
      language,
      direction,
      isLoading,
      detectionFailed,
      t,
      setLanguage,
      toggleLanguage,
    }),
    [country, isIran, language, direction, isLoading, detectionFailed, t, setLanguage, toggleLanguage]
  );

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
}

export function useRegion(): RegionContextValue {
  const ctx = useContext(RegionContext);
  if (!ctx) {
    throw new Error('useRegion must be used within RegionProvider');
  }
  return ctx;
}
